import { useMemo } from "react";
import { Button, cn, createDataTableColumnHelper, DataTable, TableCellStack } from "@beweegbaas/ui";
import { createFileRoute, Link, Outlet, stripSearchParams } from "@tanstack/react-router";
import { DataTableActions } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { LessonsSubnavigation } from "#/components/lessons-subnavigation";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { ShortDate } from "#/components/short-date";
import { KlassensInsightsSidebarFilters } from "#/modules/klassen.insights/components/klassen-insights-sidebar-filters";
import { getInsightsActivities } from "#/modules/klassen.insights/insights.functions";
import { getProgressStatusRowColor } from "#/modules/klassen.insights/progress-status";
import {
  INSIGHTS_SEARCH_DEFAULTS,
  insightsSearchSchema,
} from "#/modules/klassen.insights/schema.search";
import { startLessonFlowSearchSchema } from "#/modules/les.start-flow/filters";
import { getLeerlijnBeweegthemaLabel } from "#/modules/util.i18n/enum-mapping";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lessons/te-behandelen",
)({
  validateSearch: insightsSearchSchema,
  // Keeps the boolean/array defaults out of the URL.
  search: { middlewares: [stripSearchParams(INSIGHTS_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses.
  loaderDeps: ({ search }) => ({
    klasId: search.klasId,
    from: search.from,
    to: search.to,
    includeWithProgress: search.includeWithProgress,
    includeWithPartialProgress: search.includeWithPartialProgress,
    includeWithoutProgress: search.includeWithoutProgress,
    includeWithDefaults: search.includeWithDefaults,
    includeWithCustom: search.includeWithCustom,
    selectedLeerlijnFilterIds: search.selectedLeerlijnFilterIds,
    selectedBeweegactiviteitenByKlas: search.selectedBeweegactiviteitenByKlas,
  }),
  loader: ({ deps }) => getInsightsActivities({ data: { filters: deps } }),
  component: LessenTeBehandelen,
  errorComponent: TeBehandelenError,
});

type ProgressItem = Awaited<
  ReturnType<typeof getInsightsActivities>
>["beweegactiviteitProgressItems"][number];

const helper = createDataTableColumnHelper<ProgressItem>();

function ResultatenProgress({
  label,
  value,
  max,
  status,
}: {
  label: string;
  value: number;
  max: number;
  status: ProgressItem["progressStatus"];
}) {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex w-14 flex-col gap-1">
      <span className={cn("tabular-nums", value === 0 && "text-muted-foreground")}>{label}</span>
      <div aria-hidden className="h-1 overflow-hidden rounded-full bg-black/10 dark:bg-white/15">
        <div
          className={cn(
            "h-full rounded-full",
            status === "Complete" ? "bg-green-600" : "bg-amber-500",
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function LessenTeBehandelen() {
  const { locale, schoolyear, school } = Route.useParams();
  const { beweegactiviteitProgressItems, leerlijnFilter, hasSelectedActivities, selectedKlasId } =
    Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // Explicit URL selection first, else the server-resolved default (first class);
  // the URL stays param-free on first visit.
  const currentKlasId = search.klasId ?? selectedKlasId ?? undefined;
  const selectedActivitiesByKlas = search.selectedBeweegactiviteitenByKlas;
  const selectedActivities = currentKlasId ? selectedActivitiesByKlas[currentKlasId] || [] : [];

  const setSelectedActivities = (selected: string[]) => {
    // Key the write by the EFFECTIVE class — the explicit URL `klasId` or the server-resolved
    // default. The URL only carries the class on explicit selection (no `?klasId=<first>`
    // redirect anymore), so gating on `search.klasId` would drop every checkbox click on a
    // first, param-free visit. The loader resolves the same default, so a selection keyed by
    // it stays consistent. Reads `prev` at navigation time so rapid writes never clobber
    // each other.
    if (!currentKlasId) return;
    const klasId = currentKlasId;
    void navigate({
      search: (prev) => {
        // Prune the klas key when its selection empties: `stripSearchParams` only removes the
        // param when the WHOLE record deep-equals the `{}` default, so a leftover
        // `{ "<klasId>": [] }` entry would pin the param in the URL after deselecting.
        const byKlas = { ...prev.selectedBeweegactiviteitenByKlas };
        if (selected.length === 0) {
          delete byKlas[klasId];
        } else {
          byKlas[klasId] = selected;
        }
        return { ...prev, selectedBeweegactiviteitenByKlas: byKlas };
      },
      replace: true,
      resetScroll: false,
    });
  };

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("name", {
          header: "Beweegactiviteit",
          meta: { wrap: true },
          cell: ({ row, getValue }) => (
            <TableCellStack
              secondary={getLeerlijnBeweegthemaLabel(
                row.original.leerlijn,
                row.original.beweegthema,
              )}
            >
              {getValue()}
            </TableCellStack>
          ),
        }),
        helper.accessor("resultaten", {
          header: "Resultaten",
          cell: ({ row, getValue }) => (
            <ResultatenProgress
              label={getValue()}
              value={row.original.leerlingenMetResultaat}
              max={row.original.totaalLeerlingen}
              status={row.original.progressStatus}
            />
          ),
        }),
        helper.accessor("laatstBehandeld", {
          header: "Laatst behandeld",
          cell: ({ getValue }) => {
            const laatstBehandeld = getValue();
            return laatstBehandeld ? <ShortDate value={laatstBehandeld} /> : null;
          },
        }),
        helper.display({
          id: "acties",
          header: () => null,
          meta: { action: true },
          cell: ({ row }) => (
            <DataTableActions
              actions={[
                {
                  key: "details",
                  label: `Bekijk details van ${row.original.name}`,
                  icon: <IconMaterialSymbolsArrowRightAltRounded />,
                  render: (
                    <Route.Link
                      resetScroll={false}
                      to="/$locale/{-$schoolyear}/$school/lessons/te-behandelen/drawer/$beweegactiviteitId"
                      params={{
                        locale,
                        schoolyear,
                        school,
                        beweegactiviteitId: row.original.beweegactiviteitId,
                      }}
                      // The drawer detail loader reads `klasId` from the (inherited) search. The
                      // default view no longer rewrites the URL with `?klasId=<first>`, so carry the
                      // effective class into the drawer link for first (param-free) visits.
                      search={(prev) => ({
                        ...prev,
                        klasId: prev.klasId ?? currentKlasId,
                      })}
                    />
                  ),
                },
              ]}
            />
          ),
        }),
      ]),
    [locale, schoolyear, school, currentKlasId],
  );

  return (
    <Page>
      <Page.Header>
        <Page.Title>{m["nav.te_behandelen"]()}</Page.Title>
        <Page.HeaderActions>
          <Page.Header.MainAction>
            <Button
              disabled={!hasSelectedActivities}
              variant="default"
              size="xl"
              nativeButton={false}
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/start/les-flow/3"
                  params={(prev: Record<string, string>) => prev}
                  // The target route's `stripSearchParams` middleware strips default-valued keys
                  // back off, so parsing full defaults here and letting the router encode them
                  // reproduces the same URL the old string serializer built.
                  search={startLessonFlowSearchSchema.parse({
                    klasIds: currentKlasId ? [currentKlasId] : [],
                    beweegactiviteiten: selectedActivities,
                  })}
                />
              }
            >
              Start een les
            </Button>
          </Page.Header.MainAction>
        </Page.HeaderActions>
      </Page.Header>
      <LessonsSubnavigation />
      <Page.Body className="min-h-0">
        <Page.Container>
          <Page.SidebarLayout>
            <Page.Sidebar>
              <KlassensInsightsSidebarFilters filterTree={leerlijnFilter} />
            </Page.Sidebar>
            <PendingContent className="grow">
              <DataTable
                card
                aria-label="Behandelde beweegactiviteiten"
                columns={columns}
                data={beweegactiviteitProgressItems}
                getRowId={(item) => item.beweegactiviteitId}
                selectedIds={selectedActivities}
                onSelectedIdsChange={setSelectedActivities}
                selectAllLabel={m["lessen.selecteer_alle_beweegactiviteiten"]()}
                getSelectLabel={(item) => `Selecteer ${item.name}`}
                getRowClassName={(item) => getProgressStatusRowColor(item.progressStatus)}
                empty={m["common.empty_state.no_results_found"]()}
              />
            </PendingContent>
          </Page.SidebarLayout>
        </Page.Container>
      </Page.Body>
      <Outlet />
    </Page>
  );
}

function TeBehandelenError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
